import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
} from 'rxjs';
import {
  LookupOption,
  LookupTypeCodes,
} from '../../../../models/lookup.models';
import {
  DescribeStepResult,
  WizardRequestor,
} from '../../../../models/request-wizard.models';
import { ResourceDto } from '../../../../models/resource.models';
import { AuthService } from '../../../../services/auth.service';
import { LookupService } from '../../../../services/lookup.service';
import { ResourceService } from '../../../../services/resource.service';
import { requestorPracticeAreaCoverageValidator } from '../../../../validators/requestor-practice-area-coverage.validator';
import { SelectOption } from '../../../shared/searchable-multi-select/searchable-multi-select.component';

/** Number of resource results to fetch for the requestor search typeahead. */
const REQUESTOR_SEARCH_PAGE_SIZE = 25;

/** Max length of the request description. Mirrors the API StringLength cap. */
const DESCRIPTION_MAX_LENGTH = 10000;

/** A practice-area row in the search dropdown, with selection/coverage flags. */
interface PracticeAreaOption extends SelectOption {
  selected: boolean;
  uncovered: boolean;
}

@Component({
  selector: 'app-describe-step',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './describe-step.component.html',
})
export class DescribeStepComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private resourceService = inject(ResourceService);
  private lookupService = inject(LookupService);
  private authService = inject(AuthService);

  @Input() initialResult: DescribeStepResult | null = null;
  @Output() resultChange = new EventEmitter<DescribeStepResult>();

  /** Max length of the request description, exposed for the template counter. */
  readonly descriptionMaxLength = DESCRIPTION_MAX_LENGTH;

  /** Toggles the red counter highlight briefly after the cap is hit. */
  descriptionLimitReached = false;

  @ViewChild('descriptionField', { read: ElementRef })
  private descriptionField?: ElementRef<HTMLElement>;

  private limitFeedbackTimeout?: ReturnType<typeof setTimeout>;

  form!: FormGroup;
  requestTypes: LookupOption[] = [];
  allPracticeAreas: SelectOption[] = [];
  requestorSuggestions: ResourceDto[] = [];
  selectedRequestors: WizardRequestor[] = [];
  isLoadingRequestor = false;
  isLoadingDefaultRequestor = false;
  requestorNotFound = false;
  requestorSearchTerm = '';

  practiceAreaSearchTerm = '';
  practiceAreaDropdownOpen = false;

  private destroy$ = new Subject<void>();
  private requestorQuery$ = new Subject<string>();

  ngOnInit(): void {
    this.form = this.fb.group({
      description: [
        '',
        [Validators.required, Validators.maxLength(DESCRIPTION_MAX_LENGTH)],
      ],
      requestType: ['PRIMARY'],
      requestorSearch: [''],
      practiceAreaCodes: [
        [] as string[],
        requestorPracticeAreaCoverageValidator(() =>
          this.selectedRequestors.map((r) => ({
            id: r.id,
            name: r.name,
            practiceAreaCodes: r.practiceAreaCodes ?? [],
          })),
        ),
      ],
    });

    if (this.initialResult) {
      this.selectedRequestors = this.initialResult.requestors ?? [];
      this.form.patchValue(
        {
          description: this.initialResult.description,
          requestType: this.initialResult.requestType,
          practiceAreaCodes: this.initialResult.practiceAreaCodes ?? [],
        },
        { emitEvent: false },
      );
    }

    this.loadRequestTypes();
    this.loadPracticeAreas();
    this.setupRequestorTypeahead();
    this.setupDefaultRequestor();

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emit());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.limitFeedbackTimeout) {
      clearTimeout(this.limitFeedbackTimeout);
    }
  }

  get uncoveredPracticeAreaCodes(): string[] {
    const selected = new Set<string>(
      this.form?.get('practiceAreaCodes')?.value ?? [],
    );
    const codes: string[] = [];
    for (const r of this.selectedRequestors) {
      const rCodes = r.practiceAreaCodes ?? [];
      if (rCodes.length === 0) continue;
      const covered = rCodes.some((c) => selected.has(c));
      if (!covered) {
        codes.push(...rCodes);
      }
    }
    return [...new Set(codes)];
  }

  get uncoveredRequestorNames(): string {
    const error = this.form
      ?.get('practiceAreaCodes')
      ?.getError('requestorCoverage');
    return error?.uncovered?.join(', ') ?? '';
  }

  /** Current length of the description, for the character counter. */
  get descriptionLength(): number {
    return (this.form?.get('description')?.value ?? '').length;
  }

  /**
   * Flags an attempt to type past the cap so we can nudge the user. The
   * textarea's maxlength already blocks the keystroke; this only fires the
   * visual feedback when a printable key would have added a character.
   */
  onDescriptionKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLTextAreaElement;
    const insertsCharacter =
      event.key === 'Enter' ||
      (event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey);
    if (!insertsCharacter) return;

    // Replacing a selection keeps the length within the cap, so allow it.
    const hasSelection = target.selectionStart !== target.selectionEnd;
    if (hasSelection) return;

    if (target.value.length >= this.descriptionMaxLength) {
      this.flashDescriptionLimit();
    }
  }

  /** Fires the same feedback when a paste would overflow the cap. */
  onDescriptionPaste(event: ClipboardEvent): void {
    const target = event.target as HTMLTextAreaElement;
    const pasted = event.clipboardData?.getData('text') ?? '';
    const selectionLength =
      (target.selectionEnd ?? 0) - (target.selectionStart ?? 0);
    const resultingLength =
      target.value.length - selectionLength + pasted.length;
    if (resultingLength > this.descriptionMaxLength) {
      this.flashDescriptionLimit();
    }
  }

  /** Highlights the counter red and shakes the field (unless reduced-motion). */
  private flashDescriptionLimit(): void {
    this.descriptionLimitReached = true;
    if (this.limitFeedbackTimeout) clearTimeout(this.limitFeedbackTimeout);
    this.limitFeedbackTimeout = setTimeout(() => {
      this.descriptionLimitReached = false;
    }, 1000);

    const el = this.descriptionField?.nativeElement;
    if (!el || typeof el.animate !== 'function') return;
    const prefersReducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReducedMotion) return;

    el.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-5px)' },
        { transform: 'translateX(5px)' },
        { transform: 'translateX(-3px)' },
        { transform: 'translateX(3px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: 280, easing: 'ease-in-out' },
    );
  }

  /** Human-readable list of what's still required on this step. */
  getValidationIssues(): string[] {
    const issues: string[] = [];
    if (this.form?.get('description')?.hasError('required')) {
      issues.push('Enter a request description.');
    }
    if (this.form?.get('description')?.hasError('maxlength')) {
      issues.push(
        `Shorten the request description to ${this.descriptionMaxLength} characters or fewer.`,
      );
    }
    if (this.form?.get('practiceAreaCodes')?.hasError('requestorCoverage')) {
      const names = this.uncoveredRequestorNames;
      issues.push(
        names
          ? `Add a practice area covering each requestor (missing: ${names}).`
          : 'Add at least one practice area for each requestor.',
      );
    }
    return issues;
  }

  private get practiceAreaCodes(): string[] {
    return this.form?.get('practiceAreaCodes')?.value ?? [];
  }

  get selectedPracticeAreaCount(): number {
    return this.practiceAreaCodes.length;
  }

  /** Selected practice areas, resolved to {value, label} for the chip list. */
  get selectedPracticeAreaOptions(): SelectOption[] {
    return this.practiceAreaCodes.map((code) => ({
      value: code,
      label:
        this.allPracticeAreas.find((pa) => pa.value === code)?.label ?? code,
    }));
  }

  /** Codes that still need selecting to satisfy requestor coverage. */
  get uncoveredPracticeAreaCodesSet(): Set<string> {
    return new Set(this.uncoveredPracticeAreaCodes);
  }

  /**
   * Full practice-area list for the dropdown, filtered by the search term.
   * Selected options are flagged (and shown with a check), as are coverage-
   * required ones (shown red). Sort order: selected first, then coverage-
   * required, then the rest.
   */
  get practiceAreaOptions(): PracticeAreaOption[] {
    const selected = new Set(this.practiceAreaCodes);
    const uncovered = this.uncoveredPracticeAreaCodesSet;
    const term = this.practiceAreaSearchTerm.trim().toLowerCase();
    const matching = this.allPracticeAreas
      .filter((pa) => !term || pa.label.toLowerCase().includes(term))
      .map((pa) => ({
        value: pa.value,
        label: pa.label,
        selected: selected.has(pa.value),
        uncovered: uncovered.has(pa.value),
      }));
    return [
      ...matching.filter((o) => o.selected),
      ...matching.filter((o) => !o.selected && o.uncovered),
      ...matching.filter((o) => !o.selected && !o.uncovered),
    ];
  }

  private loadRequestTypes(): void {
    this.lookupService
      .getLookupOptions(LookupTypeCodes.REQUEST_TYPE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (options) => (this.requestTypes = options),
        error: (err) => console.error('Error loading request types:', err),
      });
  }

  private loadPracticeAreas(): void {
    this.resourceService
      .getAllPracticeAreas()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (areas) =>
          (this.allPracticeAreas = areas.map((pa) => ({
            value: pa.practiceCode,
            label: pa.description,
          }))),
        error: (err) => console.error('Error loading practice areas:', err),
      });
  }

  private setupRequestorTypeahead(): void {
    this.requestorQuery$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        switchMap((query) => {
          if (!query || query.trim().length < 3) {
            this.isLoadingRequestor = false;
            this.requestorSuggestions = [];
            this.requestorNotFound = false;
            this.requestorSearchTerm = '';
            return [];
          }
          this.isLoadingRequestor = true;
          this.requestorSearchTerm = query;
          // Requestors can be any employee regardless of the current user's
          // practice-area access, so use the unrestricted search endpoint.
          return this.resourceService.searchAllResources({
            page: 1,
            pageSize: REQUESTOR_SEARCH_PAGE_SIZE,
            search: query,
            sortBy: 'name',
            sortDirection: 'asc',
          });
        }),
      )
      .subscribe((result) => {
        this.isLoadingRequestor = false;
        if (result && result.items) {
          const selectedIds = this.selectedRequestors.map((r) => r.id);
          this.requestorSuggestions = result.items.filter(
            (r) => !selectedIds.includes(r.employeeId),
          );
          this.requestorNotFound = this.requestorSuggestions.length === 0;
        }
      });
  }

  private setupDefaultRequestor(): void {
    if (this.selectedRequestors.length > 0) return;
    const currentUser = this.authService.getCurrentUserValue();
    if (!currentUser?.employeeId) return;

    this.isLoadingDefaultRequestor = true;
    this.resourceService.getResource(currentUser.employeeId).subscribe({
      next: (result) => {
        this.isLoadingDefaultRequestor = false;
        if (!result) return;
        const codes = result.practiceAreas?.map((pa) => pa.practiceCode) ?? [];
        this.selectedRequestors = [
          {
            id: result.employeeId,
            name: result.fullName,
            role: result.title || 'Unknown',
            isCurrentUser: true,
            practiceAreaCodes: codes,
          },
        ];
        if (codes.length > 0) {
          this.form.patchValue({ practiceAreaCodes: codes });
        }
        this.form.get('practiceAreaCodes')?.updateValueAndValidity();
        this.emit();
      },
      error: (err) => {
        this.isLoadingDefaultRequestor = false;
        console.error('Error loading default requestor:', err);
      },
    });
  }

  onRequestorInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.requestorQuery$.next(value);
  }

  onRequestorFocus(): void {
    const value = this.form.get('requestorSearch')?.value || '';
    if (value.trim().length >= 3) this.requestorQuery$.next(value);
  }

  onRequestorBlur(): void {
    // Delay clearing so a suggestion click registers first.
    setTimeout(() => (this.requestorSuggestions = []), 200);
  }

  onSelectRequestorBySearch(): void {
    if (this.requestorSuggestions.length > 0) {
      this.selectRequestor(this.requestorSuggestions[0]);
    }
  }

  selectRequestor(resource: ResourceDto): void {
    const currentUser = this.authService.getCurrentUserValue();
    const requestor: WizardRequestor = {
      id: resource.employeeId,
      name: resource.fullName,
      role: resource.title || 'Unknown',
      isCurrentUser: resource.employeeId === currentUser?.employeeId,
      practiceAreaCodes:
        resource.practiceAreas?.map((pa) => pa.practiceCode) ?? [],
    };

    if (!this.selectedRequestors.some((r) => r.id === requestor.id)) {
      this.selectedRequestors.push(requestor);
      // Auto-merge this requestor's practice areas into the selection.
      const codes = requestor.practiceAreaCodes ?? [];
      if (codes.length > 0) {
        const current: string[] =
          this.form.get('practiceAreaCodes')?.value ?? [];
        const merged = [...new Set([...current, ...codes])];
        this.form.patchValue({ practiceAreaCodes: merged });
      }
      this.form.get('practiceAreaCodes')?.updateValueAndValidity();
    }

    this.requestorSuggestions = [];
    this.requestorNotFound = false;
    this.requestorSearchTerm = '';
    this.form.patchValue({ requestorSearch: '' }, { emitEvent: false });
    this.form.markAsDirty();
    this.emit();
  }

  removeRequestor(requestorId: string): void {
    const removed = this.selectedRequestors.find((r) => r.id === requestorId);
    this.selectedRequestors = this.selectedRequestors.filter(
      (r) => r.id !== requestorId,
    );

    // Deselect the removed requestor's practice areas that no remaining
    // requestor still holds. Manually-added codes are preserved because they
    // were never part of the removed requestor's code list.
    if (removed && (removed.practiceAreaCodes?.length ?? 0) > 0) {
      const remainingUnion = new Set<string>(
        this.selectedRequestors.flatMap((r) => r.practiceAreaCodes ?? []),
      );
      const codesToDrop = new Set<string>(
        (removed.practiceAreaCodes ?? []).filter(
          (code) => !remainingUnion.has(code),
        ),
      );
      if (codesToDrop.size > 0) {
        const current: string[] =
          this.form.get('practiceAreaCodes')?.value ?? [];
        const updated = current.filter((code) => !codesToDrop.has(code));
        this.form.patchValue({ practiceAreaCodes: updated });
      }
    }

    this.form.get('practiceAreaCodes')?.updateValueAndValidity();
    this.form.markAsDirty();
    this.emit();
  }

  onPracticeAreaFocus(): void {
    this.practiceAreaDropdownOpen = true;
  }

  onPracticeAreaInput(event: Event): void {
    this.practiceAreaSearchTerm = (event.target as HTMLInputElement).value;
    this.practiceAreaDropdownOpen = true;
  }

  onPracticeAreaBlur(): void {
    // Delay closing so a suggestion click registers first.
    setTimeout(() => (this.practiceAreaDropdownOpen = false), 200);
  }

  /** Adds the practice area if not selected, removes it if already selected. */
  togglePracticeArea(code: string): void {
    const current = this.practiceAreaCodes;
    const next = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code];
    this.form.patchValue({ practiceAreaCodes: next });
    this.form.get('practiceAreaCodes')?.updateValueAndValidity();
    this.form.markAsDirty();
    this.emit();
  }

  removePracticeArea(code: string): void {
    this.form.patchValue({
      practiceAreaCodes: this.practiceAreaCodes.filter((c) => c !== code),
    });
    this.form.get('practiceAreaCodes')?.updateValueAndValidity();
    this.form.markAsDirty();
    this.emit();
  }

  private emit(): void {
    this.resultChange.emit({
      description: this.form.get('description')?.value || '',
      requestType: this.form.get('requestType')?.value || 'PRIMARY',
      requestors: this.selectedRequestors,
      practiceAreaCodes: this.form.get('practiceAreaCodes')?.value ?? [],
    });
  }
}
